import { Box, Stack, Typography } from '@mui/material'
import { spacing } from 'c1ui'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { EmptyList } from 'components/core/empty-list'
import List from 'components/core/list'
import { C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { C1TableReduxHook } from 'components/core/list/C1Table/C1TableReduxHook'
import { TaskViewLinkV2 } from 'components/core/task-list-v2/task-list-v2-components/TaskViewLinkV2'
import { RelativeTimestamp } from 'components/core/time/display'
import { AppUserDisplayName } from 'components/pages/app/users/app-user-display-name'
import { C1EntitlementDisplayNameWithFetch } from 'components/pages/entitlement/display-name'
import { GrantFeedDrawerPageHeaderHeight } from 'components/pages/entitlement/grant-feed/GrantFeedDrawer'
import { ExportData } from 'components/pages/explore/query/ExportData'
import { AppEntitlementUserBindingFeedValue } from 'lib/actions/apps/app-entitlement'
import { useInitialFilters } from 'lib/search'
import { AppEntitlementWithExpired } from 'pbts/c1/api/app/v1/app_entitlement'
import { GrantEventType, SearchGrantFeedRequest } from 'pbts/c1/api/app/v1/app_entitlement_user_binding'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { searchGrantFeedApi } from 'redux_toolkit/entitlements'
import { GrantFeedFilter } from './GrantFeedFilter'

export enum GrantFeedColumns {
    date,
    event,
    entitlement,
}

export const colsMap: Record<GrantFeedColumns, C1TableReduxColumn<AppEntitlementUserBindingFeedValue>> = {
    [GrantFeedColumns.entitlement]: {
        field: 'entitlement',
        label: <FormattedMessage defaultMessage="Entitlement" id="components_past_grants_entitlement" />,
        flex: 1,
        Component: ({ row }) => (row.appId && row.appEntitlementId) && (
            <C1EntitlementDisplayNameWithFetch appId={row.appId} entitlementId={row.appEntitlementId} textTruncationWidth="400px" />
        ),
    },
    [GrantFeedColumns.event]: {
        field: 'appUserId',
        flex: 2,
        label: <FormattedMessage defaultMessage="Event detail" id="components_past_grants_account" />,
        Component: ({ row }) => <>
            <Stack alignItems="center" direction="row" spacing={1}>
                <Typography component="span" variant="h6">
                    {row.eventType === GrantEventType.ADDED && row.ticket && <FormattedMessage defaultMessage="Request: via ConductorOne" id="components_grant_added" />}
                    {row.eventType === GrantEventType.ADDED && !row.ticket && <FormattedMessage defaultMessage="Discovered: Grant added" id="components_grant_discovered" />}
                    {row.eventType === GrantEventType.REMOVED && row.ticket && <FormattedMessage defaultMessage="Revoke: via ConductorOne" id="components_grant_revoked" />}
                    {row.eventType === GrantEventType.REMOVED && !row.ticket && <FormattedMessage defaultMessage="Discovered: Grant removed" id="components_revoke_discovered" />}
                </Typography>
                <Typography>
                    <FormattedMessage defaultMessage="for" id="components_grant_for" />
                </Typography>
            </Stack>
            <AppUserDisplayName appUser={row.appUser} includeDetails={false} />
            {row.ticket && <Box ml={2}><TaskViewLinkV2 task={row.ticket} /></Box>}
        </>,
    },
    [GrantFeedColumns.date]: {
        field: 'date',
        label: <FormattedMessage defaultMessage="Date" id="components_past_grants_date" />,
        flex: 0,
        Component: ({ row }) => (row.date
            ? <RelativeTimestamp date={row.date} variant="date" />
            : <C1EmptyIndicator />
        ),
    },
}

export type GrantFeedSearchFilter = Partial<SearchGrantFeedRequest>

type GrantFeedProps = {
    filter: GrantFeedSearchFilter
    columns?: GrantFeedColumns[]
}

export const GrantFeed: FC<GrantFeedProps> = ({
    filter: _filter,
    columns = [GrantFeedColumns.event, GrantFeedColumns.date],
}) => {
    const initialFilters = useInitialFilters(SearchGrantFeedRequest)
    const emptyReq = useMemo<Partial<SearchGrantFeedRequest>>(() => ({
        ..._filter,
        expandMask: { paths: ['app_user_id', 'ticket_id'] },
    }), [_filter])

    const [filter, setFilter] = useState<SearchGrantFeedRequest>(
        SearchGrantFeedRequest.create({
            ...initialFilters,
            ...emptyReq,
        }),
    )

    const onFilterChange = useCallback((f: SearchGrantFeedRequest) => {
        SearchGrantFeedRequest.mergePartial(f, emptyReq)
        setFilter(f)
    }, [emptyReq])

    const appId = useMemo(() => (
        filter.appRefs?.[0]?.id || filter.appEntitlementRefs?.[0]?.appId || filter.appUserRefs?.[0]?.appId
    ), [filter])

    const userId = useMemo(() => (
        filter.userRefs?.[0]?.id || undefined
    ), [filter])

    const exportDataRequest = useMemo(
        () => ExportDataRequest.create({
            queryId: QueryId.GRANT_FEED,
            request: {
                oneofKind: 'searchGrantFeedRequest',
                searchGrantFeedRequest: filter,
            },
        }),
        [filter],
    )

    const search = useMemo(() => (
        <Stack direction="row" spacing={1} alignItems="center">
            <GrantFeedFilter
                onFilterChange={onFilterChange}
                filter={filter}
                appId={appId}
                userId={userId}
                hideAppUserFilter={_filter.appUserRefs && _filter.appUserRefs.length > 0}
            />
            <ExportData
                exportDataRequest={exportDataRequest}
            />
        </Stack>
    ), [onFilterChange, filter, appId, userId, _filter.appUserRefs, exportDataRequest])

    const cols = useMemo(() => [...columns?.map((col) => colsMap[col]) || []], [columns])

    const table = useMemo(() => {
        return <C1TableReduxHook<SearchGrantFeedRequest, AppEntitlementUserBindingFeedValue>
            cols={cols}
            request={filter}
            hook={searchGrantFeedApi.useSearchGrantFeed}
            invalidateHook={searchGrantFeedApi.useInvalidateSearchGrantFeed}
            emptyState={<EmptyList
                text={<FormattedMessage
                    id="components_grant_feed_empty"
                    defaultMessage="No history"
                />}
            />}
        />
    }, [cols, filter])

    return <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'auto',
        maxHeight: `calc(100vh - ${GrantFeedDrawerPageHeaderHeight} - ${spacing.xl})`, // 60px header + padding
    }}
    >
        <List<AppEntitlementWithExpired> table={table} search={search} />
    </Box>
}
